import { useMemo } from 'react'
import {
  apiRunAuthorityReview,
  apiUpdateAuthorityTopicCandidate,
  apiConvertAuthorityTopicCandidate,
} from '../studioApi.js'
import { useAuthority } from '../contexts/AuthorityContext'
import type {
  AuthorityCandidateConvertResponse,
  AuthorityCandidateUpdateResponse,
  AuthorityInbox,
  AuthorityReview,
  AuthorityReviewResponse,
  AuthorityTopicCandidate,
  BootstrapPayload,
  StateSetter,
} from '../types'

export interface AuthorityHandlersApi {
  runReview: (payload: unknown) => Promise<AuthorityReviewResponse>
  updateTopicCandidate: (
    id: string,
    updates: Partial<AuthorityTopicCandidate>
  ) => Promise<AuthorityCandidateUpdateResponse>
  convertTopicCandidate: (
    id: string
  ) => Promise<AuthorityCandidateConvertResponse>
}

const DEFAULT_API: AuthorityHandlersApi = Object.freeze({
  runReview: apiRunAuthorityReview,
  updateTopicCandidate: apiUpdateAuthorityTopicCandidate,
  convertTopicCandidate: apiConvertAuthorityTopicCandidate,
})

export interface AuthorityHandlersDeps {
  setCurrentReview: StateSetter<AuthorityReview | null>
  setReviewLoading: StateSetter<boolean>
  applyInbox: (payload?: AuthorityInbox | null) => void
  applyBootstrap: (payload: BootstrapPayload) => void
  api?: AuthorityHandlersApi
}

export interface AuthorityHandlers {
  handleAuthorityReview: (
    payload: unknown
  ) => Promise<AuthorityReview | null | undefined>
  handleUpdateAuthorityTopicCandidate: (
    id: string,
    updates: Partial<AuthorityTopicCandidate>
  ) => Promise<AuthorityTopicCandidate | undefined>
  handleConvertAuthorityTopicCandidate: (
    id: string
  ) => Promise<AuthorityCandidateConvertResponse>
}

// Pure factory for authority handlers. Returns the API response payloads
// so the caller can do UI routing (App.jsx maps `convertTopicCandidate`
// output into prefill state and a tab switch — that orchestration stays
// at the App level and is not the handler's concern).
//
// Notice the absence of toast / prefill / setTab — those are pure UI
// effects. This factory is the data layer; UI orchestration sits above.
export function createAuthorityHandlers(
  deps: AuthorityHandlersDeps
): AuthorityHandlers {
  const {
    setCurrentReview,
    setReviewLoading,
    applyInbox,
    applyBootstrap,
    api = DEFAULT_API,
  } = deps

  async function handleAuthorityReview(
    payload: unknown
  ): Promise<AuthorityReview | null | undefined> {
    setReviewLoading(true)
    try {
      const data = await api.runReview(payload)
      setCurrentReview(data?.review || null)
      if (data?.inbox) applyInbox(data.inbox)
      if (data?.bootstrap) applyBootstrap(data.bootstrap)
      return data?.review
    } finally {
      setReviewLoading(false)
    }
  }

  async function handleUpdateAuthorityTopicCandidate(
    id: string,
    updates: Partial<AuthorityTopicCandidate>
  ): Promise<AuthorityTopicCandidate | undefined> {
    const data = await api.updateTopicCandidate(id, updates)
    if (data?.inbox) applyInbox(data.inbox)
    if (data?.bootstrap) applyBootstrap(data.bootstrap)
    return data?.candidate
  }

  // Returns the full API response (including `prefill`) — App.jsx wraps
  // this to drive the prefill state + tab switch.
  async function handleConvertAuthorityTopicCandidate(
    id: string
  ): Promise<AuthorityCandidateConvertResponse> {
    const data = await api.convertTopicCandidate(id)
    if (data?.inbox) applyInbox(data.inbox)
    if (data?.bootstrap) applyBootstrap(data.bootstrap)
    return data
  }

  return {
    handleAuthorityReview,
    handleUpdateAuthorityTopicCandidate,
    handleConvertAuthorityTopicCandidate,
  }
}

export interface UseAuthorityHandlersOptions {
  applyBootstrap: (payload: BootstrapPayload) => void
}

export function useAuthorityHandlers(
  options: UseAuthorityHandlersOptions
): AuthorityHandlers {
  const { applyBootstrap } = options
  const { setCurrentReview, setReviewLoading, applyInbox } = useAuthority()
  return useMemo(
    () =>
      createAuthorityHandlers({
        setCurrentReview,
        setReviewLoading,
        applyInbox,
        applyBootstrap,
      }),
    [setCurrentReview, setReviewLoading, applyInbox, applyBootstrap]
  )
}

export const __test__ = { DEFAULT_API }
